/**
 * Portfolio Module - Complete v5.0.1 Redesign
 * 60/40 Split: Database View (left) | AI Analysis (right)
 */

import AppState from '../../core/AppState.js';
import Config from '../../core/Config.js';
import EventBus, { AppEvents } from '../../core/EventBus.js';
import ApiService from '../../services/ApiService.js';
import PromptService from '../../services/PromptService.js';
import StorageService from '../../services/StorageService.js';
import InitiativeService from '../../services/InitiativeService.js';
import container from '../../core/ServiceContainer.js';
import { SERVICE_TOKENS as T } from '../../core/services.tokens.js';
import { bulkUpload } from '../../components/BulkUpload.js';
import { ModalService } from '../../ui-kit/ModalService.jsx';
import { portfolioStore } from '../../store/portfolioStore.js';
import { bindPromptEditors } from '../../components/PromptBinder.js';
import { bindSafeClick } from '../../utils/SafeClick.js';
import * as ActionRegistry from '../../core/ActionRegistry.js';
import { getDatasetContext } from '../../utils/dataset.js';
import { installSplit } from '../../utils/Splitter.js';
import { createLayoutPanel, createPanelActions } from '../../utils/panel.js';

export class PortfolioModule {
  constructor() {
    this.container = null;
    this.initiatives = [];
    this.filteredInitiatives = [];
    this.currentView = 'database';
    // Conversational analysis state
    this.history = [];
    this.searchTerm = '';
    this.filterType = 'all';
    this.filterStatus = 'all';
    this.filterRisk = 'all';
    this._lastScoredAt = null;
    this._lastScoredCount = 0;
    this._depsCount = new Map();
    this._riskMap = new Map();
  }

  async initialize() {
    // Resolve service via DI (fallback to direct import)
    const svc = container.get(T.INITIATIVES) || InitiativeService;
    this._svc = svc;
    if (!this._svc.initialized) {
      try { await this._svc.initialize(); } catch {}
    }
    // Load initiatives via service
    this.loadInitiatives();
    
    // Subscribe to initiative updates
    const refresh = () => { this.loadInitiatives(); this.render(); };
    EventBus.on(AppEvents.INITIATIVE_ADDED, refresh);
    EventBus.on(AppEvents.INITIATIVE_UPDATED, refresh);
    EventBus.on(AppEvents.INITIATIVE_DELETED, refresh);
    
    // Refresh when initiatives are cleared or deleted in bulk
    EventBus.on(AppEvents.INITIATIVES_CLEARED, () => {
      this.loadInitiatives();
      this.render();
    });
    
    // Subscribe to bulk import completion
    EventBus.on(AppEvents.BULK_IMPORT_COMPLETE, (data) => {
      console.log('🔄 Bulk import complete, refreshing portfolio...');
      // Small delay to ensure data is saved
      setTimeout(() => {
        this.loadInitiatives();
        this.render();
        if (data.successCount > 0) {
          console.log(`✅ Portfolio refreshed with ${data.successCount} new initiatives`);
        }
      }, 100);
    });
  }

  mount(container) {
    this.container = container;
    this.render();
  }

  // Public method used by App.js to refresh view after saves
  refresh() {
    this.loadInitiatives();
    if (this.container) {
      this.render();
    }
  }

  loadInitiatives() {
    try {
      const svc = this._svc || InitiativeService;
      this.initiatives = svc.getInitiatives();
    } catch {
      this.initiatives = [];
    }
    this.filteredInitiatives = [...this.initiatives];
    try { portfolioStore.setInitiatives(this.initiatives); } catch (_) {}
    console.log(`📊 Portfolio loaded ${this.initiatives.length} initiatives`);
    // Track last scored timestamp from initiatives
    try {
      const times = this.initiatives
        .map(i => Date.parse(i.analysisAt || 0))
        .filter(n => Number.isFinite(n));
      const ts = Math.max(0, ...times);
      this._lastScoredAt = ts > 0 ? new Date(ts) : null;
      this._lastScoredCount = ts > 0 ? this.initiatives.filter(i => Date.parse(i.analysisAt || 0) === ts).length : 0;
    } catch (_) { this._lastScoredAt = null; this._lastScoredCount = 0; }

    // Refresh dependency/risk indicators (best-effort)
    this.refreshGraphIndicators();
  }

  async refreshGraphIndicators() {
    try {
      const { default: db } = await import('../../services/db/DexieDB.js');
      const deps = await db.dependencies.toArray();
      const counts = new Map();
      deps.forEach(e => {
        const k = String(e.fromId);
        counts.set(k, (counts.get(k) || 0) + 1);
      });
      this._depsCount = counts;
      const risks = await db.risks.toArray();
      const rmap = new Map();
      risks.forEach(r => { rmap.set(String(r.initiativeId), r.level || null); });
      this._riskMap = rmap;
    } catch (_) {}
  }

  render() {
    if (!this.container) return;

    this.container.innerHTML = `
      <div class="portfolio-module">
        <div class="portfolio-header" style="display:flex;align-items:center;justify-content:space-between;gap:12px">
          <div></div>
          <div style="display:flex;align-items:center;gap:12px">
            <div class="prompt-editors-analysis" style="margin:0">
              <button class="prompt-edit-btn btn-small" 
                      data-prompt-key="portfolioAnalysis"
                      data-prompt-name="Portfolio Analysis">
                <span>Edit Portfolio Analysis Prompt</span>
              </button>
              <button class="prompt-edit-btn btn-small" 
                      data-prompt-key="metricsAssistant"
                      data-prompt-name="Metrics Assistant">
                <span>Edit Metrics Assistant Prompt</span>
              </button>
            </div>
          </div>
        </div>

        <div class="portfolio-split-layout">
          <!-- Left Side: Database View (60%) -->
          ${(() => {
            const actions = createPanelActions([
              { label: 'Score Fit', action: 'portfolio-score-fit', variant: 'primary', size: 'small', attrs: { title: 'Score fit vs Uploaded Strategy' } },
              { label: 'Bulk Upload', action: 'portfolio-bulk-upload', variant: 'primary', size: 'small', attrs: { id: 'bulk-upload-btn', title: 'Import initiatives from CSV/Excel' } }
            ]);
            const content = `
              <div class="database-controls">
                <input 
                  type="text"
                  class="search-input" 
                  placeholder="Search initiatives..."
                  value="${this.searchTerm}"
                  id="portfolio-search-input"
                  autocomplete="off"
                  data-lpignore="true"
                >
              </div>
              <div class="database-table-container">
                ${this.renderDatabaseTable()}
              </div>
            `;
            return createLayoutPanel({
              title: 'Initiative Database',
              actions,
              content,
              className: 'portfolio-database'
            });
          })()}

          <!-- Right Side: AI Conversational Analysis (40%) -->
          <div class="portfolio-analysis-wrapper">
            <div id="analysis-island" style="height:100%"></div>
          </div>
        </div>
      </div>
    `;

    this.attachEventHandlers();
  }

  renderDatabaseTable() {
    if (this.filteredInitiatives.length === 0) {
      return `
        <div class="empty-state">
          <p>No initiatives found</p>
          <p class="text-muted">Add initiatives from the Objectives tab or use bulk upload</p>
        </div>
      `;
    }

    return `
      <table class="database-table">
        <thead>
          <tr>
            <th>Title</th>
            <th title="Normalized status">Status</th>
            <th>Strategic Fit</th>
            ${Config.FEATURES?.DEPENDENCIES ? '<th>Depends</th>' : ''}
            ${Config.FEATURES?.RISKS ? '<th>Risk</th>' : ''}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          ${this.filteredInitiatives.map((initiative, idx) => {
            return `
            <tr data-row-index="${idx}">
              <td class="title-cell">${initiative.title || initiative.objective || 'Untitled'}</td>
              <td>${this.renderStatusBadge(initiative.status || '')}</td>
              <td>
                <div class="fit-score ${this.getFitClass(initiative.strategicFit)}">
                  ${initiative.strategicFit || 'N/A'}%
                </div>
              </td>
              ${Config.FEATURES?.DEPENDENCIES ? `<td>${this._depsCount.get(String(initiative.id)) || 0}</td>` : ''}
              ${Config.FEATURES?.RISKS ? `<td>${this.renderRiskChip(this._riskMap.get(String(initiative.id)))}</td>` : ''}
              <td>
                <button class="btn btn-primary btn-small" type="button" data-action="portfolio-show-details" data-id="${initiative.id || ''}" data-index="${idx}">Show Details</button>
              </td>
            </tr>
          `;}).join('')}
        </tbody>
      </table>
    `;
  }

  renderRiskChip(level) {
    if (!level) return '<span class="badge">—</span>';
    const lvl = Number(level);
    const txt = lvl >= 4 ? 'High' : (lvl === 3 ? 'Medium' : 'Low');
    const color = lvl >= 4 ? 'var(--error-color)' : (lvl === 3 ? 'var(--color-warning)' : 'var(--success-color)');
    return `<span class="badge" style="background:transparent;color:${color};border:1px solid ${color};border-radius:10px;padding:2px 6px">${txt}</span>`;
  }

  renderStatusBadge(statusRaw) {
    const s = String(statusRaw || '').toLowerCase();
    let txt = 'Planned';
    let color = '#6b7280';
    if (s.includes('block') || s.includes('hold') || s.includes('delay')) { txt='Blocked'; color='var(--error-color)'; }
    else if (s.includes('risk')) { txt='At Risk'; color='var(--color-warning)'; }
    else if (s.includes('progress')) { txt='In Progress'; color='var(--kone-primary)'; }
    else if (s.includes('complete') || s.includes('done')) { txt='Completed'; color='var(--success-color)'; }
    return `<span class="badge" style="background:transparent;color:${color};border:1px solid ${color};border-radius:10px;padding:2px 6px">${txt}</span>`;
  }

  renderKPIBar() {
    try {
      const total = this.initiatives.length;
      const fitVals = this.initiatives.map(i => Number(i.strategicFit)).filter(n => Number.isFinite(n));
      const avgFit = fitVals.length ? Math.round(fitVals.reduce((a,b)=>a+b,0) / fitVals.length) : 0;
      const highRisks = Array.from(this._riskMap.values()).filter(v => Number(v) >= 4).length;
      const blockers = 0; // simple placeholder; advanced blocker count computed in Metrics engine when needed
      return `<div class="kpi-bar">
        <div class="kpi-card"><div class="kpi-title">Avg Strategic Fit</div><div class="kpi-value">${avgFit}%</div></div>
        <div class="kpi-card"><div class="kpi-title">High Risks</div><div class="kpi-value">${highRisks}</div></div>
        <div class="kpi-card"><div class="kpi-title">Initiatives</div><div class="kpi-value">${total}</div></div>
      </div>`;
    } catch (_) { return ''; }
  }

  async openCreateWizard() {
    const { ModalService } = await import('../../ui-kit/ModalService.jsx');
    const all = this.initiatives || [];
    const options = all.map(i => `<option value="${i.id}">${(i.title || i.objective || 'Untitled').replace(/</g,'&lt;')}</option>`).join('');
    let step = 1;
    const render = () => {
      if (step === 1) return `
        <div class="form-grid">
          <div id="wiz-error" style="display:none;color:var(--error-color);margin-bottom:8px"></div>
          <label>Title<input id="wiz-title" class="form-control" /></label>
          <label>Objective<textarea id="wiz-objective" rows="2" class="form-control"></textarea></label>
          <label>Owner<input id="wiz-owner" class="form-control" /></label>
          <div style="display:flex;gap:8px">
            <label>Type
              <select id="wiz-type" class="form-control"><option>Initiative</option><option>Goal</option><option>Challenge</option><option>Demand</option></select>
            </label>
            <label>Priority
              <select id="wiz-priority" class="form-control"><option>medium</option><option>low</option><option>high</option><option>critical</option></select>
            </label>
            <label>Status
              <select id="wiz-status" class="form-control"><option>not-started</option><option>in-progress</option><option>at-risk</option><option>completed</option></select>
            </label>
          </div>
        </div>`;
      if (step === 2) return `
        <div>
          <p>Key Results (one per line)</p>
          <textarea id="wiz-krs" rows="5" class="form-control" placeholder="KR 1\nKR 2\nKR 3"></textarea>
        </div>`;
      if (step === 3) return `
        <div>
          <p>Select dependencies (optional)</p>
          <select id="wiz-deps" multiple size="8" style="width:100%">${options}</select>
        </div>`;
      return `
        <div>
          <label>Risk Level (1–5)<input id="wiz-risk" type="number" min="1" max="5" class="form-control" /></label>
          <label>Risk Note<textarea id="wiz-risk-note" rows="3" class="form-control"></textarea></label>
        </div>`;
    };
    const doRender = (modal) => { modal.setContent(render()); };
    const validateStep = () => {
      if (step !== 1) return true;
      const title = document.getElementById('wiz-title')?.value?.trim() || '';
      const objective = document.getElementById('wiz-objective')?.value?.trim() || '';
      const owner = document.getElementById('wiz-owner')?.value?.trim() || '';
      const errs = [];
      if (title.length < 3) errs.push('Title must be at least 3 characters.');
      if (objective.length < 10) errs.push('Objective must be at least 10 characters.');
      if (!owner) errs.push('Owner is required.');
      const box = document.getElementById('wiz-error');
      if (errs.length) {
        if (box) { box.style.display = 'block'; box.textContent = errs.join(' '); }
        return false;
      }
      if (box) box.style.display = 'none';
      return true;
    };
    const modal = ModalService.open({
      title: 'New Initiative',
      content: render(),
      actions: [
        { text: 'Back', class: 'uk-btn', action: () => { if (step>1) { step--; doRender(modal); } } },
        { text: 'Next', class: 'uk-btn uk-btn-primary', action: () => { if (step<4) { if (!validateStep()) return; step++; doRender(modal); } } },
        { text: 'Save', class: 'uk-btn uk-btn-primary', action: async () => {
          try {
            if (!validateStep()) return;
            const title = document.getElementById('wiz-title')?.value?.trim();
            const objective = document.getElementById('wiz-objective')?.value?.trim();
            const owner = document.getElementById('wiz-owner')?.value?.trim();
            const type = document.getElementById('wiz-type')?.value || 'Initiative';
            const priority = document.getElementById('wiz-priority')?.value || 'medium';
            const status = document.getElementById('wiz-status')?.value || 'not-started';
            const krs = (document.getElementById('wiz-krs')?.value || '').split(/\n/).map(s=>s.trim()).filter(Boolean);
            const deps = Array.from(document.getElementById('wiz-deps')?.selectedOptions || []).map(o=>o.value);
            const riskLevel = parseInt(document.getElementById('wiz-risk')?.value || '0', 10) || null;
            const riskNote = document.getElementById('wiz-risk-note')?.value || '';
            const rec = { id:`init_${Date.now()}_${Math.random().toString(36).slice(2,9)}`, title, objective, owner, type, priority, status, keyResults:krs, createdAt:new Date().toISOString(), updatedAt:new Date().toISOString() };
            await InitiativeService.createInitiative(rec);
            const { setDependenciesFor, setRiskFor } = await import('../../services/GraphService.js');
            if (deps.length) await setDependenciesFor(rec.id, deps, 'depends_on');
            if (riskLevel || riskNote) await setRiskFor(rec.id, { level: riskLevel || null, note: riskNote });
            this.loadInitiatives(); this.render();
          } catch (e) { console.warn('Create wizard save failed', e); }
        } },
        { text: 'Close', class: 'uk-btn uk-btn-secondary', action: () => {} }
      ]
    });
  }

  // Conversational analysis helpers
  getChatContainer() {
    return this.container?.querySelector?.('#portfolio-chat-messages');
  }

  appendMessage(role, text) {
    const chat = this.getChatContainer();
    if (!chat) return;
    const wrap = document.createElement('div');
    wrap.className = `message ${role}`;
    const avatar = document.createElement('div');
    avatar.className = 'message-avatar';
    avatar.textContent = role === 'user' ? 'You' : 'AI';
    const content = document.createElement('div');
    content.className = 'message-content';
    const textEl = document.createElement('div');
    textEl.className = 'message-text';
    textEl.innerHTML = this.formatAsParagraphs(String(text || ''));
    content.appendChild(textEl);
    if (role === 'assistant') {
      const meta = document.createElement('div');
      meta.className = 'message-meta';
      const small = document.createElement('small');
      small.className = 'timestamp';
      small.textContent = new Date().toLocaleString();
      meta.appendChild(small);
      content.appendChild(meta);
    }
    wrap.appendChild(avatar);
    wrap.appendChild(content);
    chat.appendChild(wrap);
    chat.scrollTop = chat.scrollHeight;
  }

  updateLastAssistantMessage(text) {
    const chat = this.getChatContainer();
    if (!chat) return;
    const nodes = chat.querySelectorAll('.message.assistant .message-content .message-text');
    const last = nodes[nodes.length - 1];
    if (last) last.innerHTML = this.formatAsParagraphs(text || '');
    chat.scrollTop = chat.scrollHeight;
  }

  formatAsParagraphs(text) {
    // Escape basic HTML and convert double newlines to paragraphs
    const esc = (s) => s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return esc(text)
      .split(/\n\n+/)
      .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
      .join('');
  }

  // Helper methods
  getActiveCount() {
    return this.initiatives.filter(i => i.status === 'active' || i.status === 'in-progress').length;
  }

  getCompletedCount() {
    return this.initiatives.filter(i => i.status === 'completed').length;
  }

  getFitClass(fit) {
    const score = parseInt(fit) || 0;
    if (score >= 80) return 'high';
    if (score >= 50) return 'medium';
    return 'low';
  }

  getAverageFit() {
    if (this.initiatives.length === 0) return 0;
    const total = this.initiatives.reduce((sum, i) => sum + (parseInt(i.strategicFit) || 0), 0);
    return Math.round(total / this.initiatives.length);
  }

  getTypeDistribution() {
    return this.initiatives.reduce((acc, i) => {
      acc[i.type || 'Initiative'] = (acc[i.type || 'Initiative'] || 0) + 1;
      return acc;
    }, {});
  }

  getRiskLevel() {
    const avgFit = this.getAverageFit();
    if (avgFit >= 70) return 'Low';
    if (avgFit >= 40) return 'Medium';
    return 'High';
  }

  getPortfolioHealth() {
    const avgFit = this.getAverageFit();
    if (avgFit >= 70) return 'strong';
    if (avgFit >= 40) return 'moderate';
    return 'weak';
  }

  getBalanceAssessment() {
    const types = Object.keys(this.getTypeDistribution()).length;
    if (types >= 3) return 'good balance';
    if (types >= 2) return 'moderate balance';
    return 'limited diversity';
  }

  getAlignmentInsight() {
    const avgFit = this.getAverageFit();
    if (avgFit >= 70) return 'Strong alignment with strategic objectives';
    if (avgFit >= 40) return 'Moderate alignment, consider refocusing some initiatives';
    return 'Weak alignment, strategic review recommended';
  }

  getResourceInsight() {
    const distribution = this.getTypeDistribution();
    const total = this.initiatives.length;
    if (total === 0) return 'No initiatives to analyze';
    const goalPercent = ((distribution.Goal || 0) / total) * 100;
    if (goalPercent >= 40) return 'Well-balanced focus on strategic goals';
    return 'Consider increasing focus on strategic goals';
  }

  getProgressInsight() {
    const completed = this.getCompletedCount();
    const active = this.getActiveCount();
    const total = this.initiatives.length;
    if (total === 0) return 'No initiatives to track';
    const completionRate = (completed / total) * 100;
    if (completionRate >= 30) return `Strong completion rate at ${Math.round(completionRate)}%`;
    return `Low completion rate at ${Math.round(completionRate)}%, focus on execution`;
  }

  getQuickWinsInsight() {
    const highFit = this.initiatives.filter(i => parseInt(i.strategicFit) >= 80).length;
    if (highFit >= 3) return `${highFit} high-impact quick wins identified`;
    return 'Limited quick win opportunities, consider tactical initiatives';
  }

  getRiskItems() {
    return [
      {
        icon: '⚠️',
        title: 'Resource Concentration',
        level: 'medium',
        description: 'Multiple initiatives competing for same resources',
        mitigation: 'Implement resource allocation matrix and phase execution'
      },
      {
        icon: '🔴',
        title: 'Strategic Gaps',
        level: 'high',
        description: 'Key strategic areas lack supporting initiatives',
        mitigation: 'Conduct gap analysis and prioritize missing initiatives'
      },
      {
        icon: '⚡',
        title: 'Execution Risk',
        level: 'low',
        description: 'Some initiatives lack clear ownership',
        mitigation: 'Assign dedicated owners and establish governance'
      }
    ];
  }

  getRecommendations() {
    return [
      {
        title: 'Prioritize High-Impact Initiatives',
        description: 'Focus resources on initiatives with >70% strategic fit score to maximize ROI'
      },
      {
        title: 'Address Strategic Gaps',
        description: 'Launch 2-3 new initiatives in underrepresented strategic areas'
      },
      {
        title: 'Improve Execution Tracking',
        description: 'Implement weekly progress reviews for all active initiatives'
      },
      {
        title: 'Optimize Resource Allocation',
        description: 'Reallocate 20% of resources from low-impact to high-impact initiatives'
      }
    ];
  }

  // Event handlers
  attachEventHandlers() {
    // Search handler - use getElementById for reliability
    const searchInput = document.getElementById('portfolio-search-input');
    if (searchInput) {
      // Use a bound function to preserve 'this' context
      const handleSearchBound = (e) => {
        this.handleSearch(e);
      };
      
      // Remove old listener if it exists
      searchInput.removeEventListener('input', searchInput._searchHandler);
      searchInput.removeEventListener('change', searchInput._searchHandler);
      
      // Store reference and add new listener
      searchInput._searchHandler = handleSearchBound;
      searchInput.addEventListener('input', handleSearchBound);
    }

    // Filter handler
    const filterSelect = document.getElementById('portfolio-filter-select');
    if (filterSelect) {
      // Use a bound function to preserve 'this' context
      const handleFilterBound = (e) => {
        this.handleFilter(e);
      };
      
      // Remove old listener if it exists
      filterSelect.removeEventListener('change', filterSelect._filterHandler);
      
      // Store reference and add new listener
      filterSelect._filterHandler = handleFilterBound;
      filterSelect.addEventListener('change', handleFilterBound);
    }

    const statusFilter = document.getElementById('portfolio-status-filter');
    if (statusFilter) {
      const handleStatusBound = (e) => { this.handleStatusFilter(e); };
      statusFilter.removeEventListener('change', statusFilter._statusHandler);
      statusFilter._statusHandler = handleStatusBound;
      statusFilter.addEventListener('change', handleStatusBound);
    }

    const riskFilter = document.getElementById('portfolio-risk-filter');
    if (riskFilter) {
      const handleRiskBound = (e) => { this.handleRiskFilter(e); };
      riskFilter.removeEventListener('change', riskFilter._riskHandler);
      riskFilter._riskHandler = handleRiskBound;
      riskFilter.addEventListener('change', handleRiskBound);
    }

    // Chip quick filters
    const chipBlocked = document.getElementById('chip-blocked');
    if (chipBlocked) chipBlocked.onclick = () => { this.filterStatus = (this.filterStatus==='blocked'?'all':'blocked'); this.applyFilters(false); };
    const chipAtRisk = document.getElementById('chip-atrisk');
    if (chipAtRisk) chipAtRisk.onclick = () => { this.filterStatus = (this.filterStatus==='at-risk'?'all':'at-risk'); this.applyFilters(false); };
    const chipHigh = document.getElementById('chip-highrisk');
    if (chipHigh) chipHigh.onclick = () => { this.filterRisk = (this.filterRisk==='high'?'all':'high'); this.applyFilters(false); };

    // (Tabs removed) — no tab handlers needed

    // Make methods available globally (fallback for any inline handlers)
    window.portfolioViewDetails = (id) => this.viewDetails(id);
    window.portfolioEditInitiative = (id) => this.editInitiative(id);
    window.portfolioRunAnalysis = () => this.runNarrativeAnalysis();
    window.openBulkUpload = () => this.openBulkUpload();
    // Inline Show Details fallback
    window.portfolioShowDetails = (id, index) => this.showDetailsModal(id, index);
    
    // Mount Preact Analysis island (no await inside non-async method)
    try {
      const host = this.container.querySelector('#analysis-island');
      if (host) {
        import('../../ui/portfolio/mountAnalysis.jsx')
          .then(mod => {
            const mount = mod.mountAnalysisIsland || mod.default;
            if (mount) mount(host);
            // Re-bind prompt editor buttons after island mounts
            try { bindPromptEditors(this.container); } catch (e) { console.warn('PromptEditor bind failed:', e); }
          })
          .catch(err => console.warn('Analysis island mount failed:', err));
      }
    } catch (e) { console.warn('Analysis island mount failed:', e); }

    // Also expose for debugging
    window.portfolioModule = this;

    // All click actions routed via ActionRouter (central registry or direct)
    this.registerRouterActions();

    // Install resizable splitter between database and analysis panels (idempotent)
    try { installSplit('.portfolio-split-layout', 'portfolio'); } catch (_) {}

    // All actions routed via ActionRegistry/ActionRouter; no manual fallbacks
  }

  handleSearch(e) {
    this.searchTerm = e.target.value.toLowerCase();
    this.applyFilters(false); // Don't re-render entire component
  }

  handleFilter(e) {
    this.filterType = e.target.value;
    this.applyFilters(false); // Don't re-render entire component
  }

  handleStatusFilter(e) { this.filterStatus = e.target.value; this.applyFilters(false); }
  handleRiskFilter(e) { this.filterRisk = e.target.value; this.applyFilters(false); }

  applyFilters(shouldRender = true) {
    this.filteredInitiatives = this.initiatives.filter(initiative => {
      const matchesSearch = !this.searchTerm || 
        (initiative.title || initiative.objective || '').toLowerCase().includes(this.searchTerm) ||
        (initiative.owner || '').toLowerCase().includes(this.searchTerm);
      
      const matchesType = this.filterType === 'all' || initiative.type === this.filterType;
      // Status normalization
      const s = String(initiative.status || '').toLowerCase();
      const norm = s.includes('block') || s.includes('hold') || s.includes('delay') ? 'blocked'
        : s.includes('risk') ? 'at-risk'
        : s.includes('progress') ? 'in-progress'
        : s.includes('complete') || s.includes('done') ? 'completed'
        : 'not-started';
      const matchesStatus = this.filterStatus === 'all' || norm === this.filterStatus;
      // Risk lookup
      let r = this._riskMap.get(String(initiative.id));
      let rBand = !r ? 'none' : (Number(r) >= 4 ? 'high' : (Number(r) === 3 ? 'medium' : 'low'));
      const matchesRisk = this.filterRisk === 'all' || rBand === this.filterRisk;

      return matchesSearch && matchesType && matchesStatus && matchesRisk;
    });
    
    // Only update the table content, not the entire component
    if (!shouldRender) {
      this.updateTableOnly();
    } else {
      this.render();
    }
  }

  updateTableOnly() {
    // Only update the table content without re-rendering the entire component
    const tableContainer = document.querySelector('.database-table-container');
    if (tableContainer) {
      tableContainer.innerHTML = this.renderDatabaseTable();
      
      // Re-attach event handlers for the action buttons in the table
      this.attachTableButtonHandlers();
    }
  }

  attachTableButtonHandlers() { /* handled by ActionRouter; no-op */ }

  attachTableButtonHandlers() { /* handled by ActionRouter; no-op */ }

  registerRouterActions() {
    const mapping = {
      'portfolio-bulk-upload': ({}) => this.openBulkUpload(),
      'portfolio-new-initiative': ({}) => this.openCreateWizard(),
      'portfolio-score-fit': ({}) => this._runAnalysisLegacy(),
      'portfolio-show-details': (ctx) => {
        try {
          const { id, index } = getDatasetContext(ctx?.target);
          this.showDetailsModal(id, index);
        } catch(_) {
          const d = ctx?.dataset || {};
          this.showDetailsModal(d.id, d.index);
        }
      },
      'portfolio-close-inline-details': ({}) => { try { document.getElementById('portfolio-inline-details')?.remove(); } catch (_) {} },
      'portfolio-edit-deps': async ({ target, dataset }) => {
        const id = dataset?.id || target?.dataset?.id;
        if (!id) return;
        await this.openDepsEditor(id);
      },
      'portfolio-edit-risk': async ({ target, dataset }) => {
        const id = dataset?.id || target?.dataset?.id;
        if (!id) return;
        await this.openRiskEditor(id);
      }
    };
    // Always register via central ActionRegistry
    ActionRegistry.registerMap('portfolio', mapping);
  }

  async openDepsEditor(initiativeId) {
    if (!Config.FEATURES?.DEPENDENCIES) return;
    const { getDependenciesFor, setDependenciesFor } = await import('../../services/GraphService.js');
    const current = await getDependenciesFor(initiativeId);
    const all = (this.initiatives || []).filter(i => String(i.id) !== String(initiativeId));
    const options = all.map(i => `<option value="${i.id}">${(i.title || i.objective || 'Untitled').replace(/</g,'&lt;')}</option>`).join('');
    const selected = new Set((current.dependsOn || []).map(e => String(e.toId)));
    const html = `
      <div>
        <p>Select initiatives this item depends on.</p>
        <select id="deps-select" multiple size="8" style="width:100%;max-height:240px">${options}</select>
        <small class="text-secondary">Hold Cmd/Ctrl to select multiple</small>
      </div>`;
    const { ModalService } = await import('../../ui-kit/ModalService.jsx');
    ModalService.open({
      title: 'Edit Dependencies',
      content: html,
      actions: [
        { text: 'Cancel', class: 'uk-btn uk-btn-secondary', action: () => {} },
        { text: 'Save', class: 'uk-btn uk-btn-primary', action: async () => {
          const sel = Array.from(document.getElementById('deps-select')?.selectedOptions || []).map(o => o.value);
          await setDependenciesFor(initiativeId, sel, 'depends_on');
        }}
      ]
    });
  }

  async openRiskEditor(initiativeId) {
    if (!Config.FEATURES?.RISKS) return;
    const { getRiskFor, setRiskFor } = await import('../../services/GraphService.js');
    const r = await getRiskFor(initiativeId) || { level:null, impact:'', likelihood:'', note:'' };
    const html = `
      <div class="form-grid">
        <label>Risk Level (1–5)
          <input id="risk-level" type="number" min="1" max="5" value="${r.level ?? ''}" class="form-control" />
        </label>
        <label>Note
          <textarea id="risk-note" rows="3" class="form-control">${(r.note||'').replace(/</g,'&lt;')}</textarea>
        </label>
      </div>`;
    const { ModalService } = await import('../../ui-kit/ModalService.jsx');
    ModalService.open({
      title: 'Edit Risk',
      content: html,
      actions: [
        { text: 'Cancel', class: 'uk-btn uk-btn-secondary', action: () => {} },
        { text: 'Save', class: 'uk-btn uk-btn-primary', action: async () => {
          const level = parseInt(document.getElementById('risk-level')?.value || '0', 10) || null;
          const note = document.getElementById('risk-note')?.value || '';
          await setRiskFor(initiativeId, { level, note });
        }}
      ]
    });
  }

  async markBlockedWithSuggestions(initiative) {
    try {
      await InitiativeService.updateInitiative(initiative.id, { status: 'blocked', updatedAt: new Date().toISOString() });
      this.loadInitiatives();
      // Suggest dependencies: recent initiatives by same owner updated within 14 days
      const owner = initiative.owner || '';
      const now = Date.now();
      const recent = (this.initiatives || []).filter(i => String(i.id) !== String(initiative.id) && (i.owner || '') === owner);
      const candidates = recent.filter(i => {
        const ts = Date.parse(i.updatedAt || i.createdAt || 0);
        return Number.isFinite(ts) && (now - ts) < (14*24*60*60*1000);
      }).slice(0, 20).map(i => ({ id: String(i.id), title: i.title || i.objective || 'Untitled' }));
      if (!candidates.length) return;
      const { ModalService } = await import('../../ui-kit/ModalService.jsx');
      const rows = candidates.map((c, idx) => `<label style="display:flex;gap:8px;align-items:center"><input type="checkbox" data-suggest-index="${idx}" value="${c.id}"> ${c.title.replace(/</g,'&lt;')}</label>`).join('');
      const html = `<div><p>Marked as <strong>Blocked</strong>. Add suggested dependencies?</p><div style="display:flex;flex-direction:column;gap:6px">${rows}</div></div>`;
      ModalService.open({
        title: 'Add Suggested Dependencies',
        content: html,
        actions: [
          { text: 'Skip', class: 'uk-btn uk-btn-secondary', action: () => {} },
          { text: 'Add', class: 'uk-btn uk-btn-primary', action: async () => {
            const picks = Array.from(document.querySelectorAll('[data-suggest-index]:checked')).map(el => el.value);
            if (picks.length) {
              const { addDependenciesFor } = await import('../../services/GraphService.js');
              await addDependenciesFor(initiative.id, picks, 'depends_on');
            }
          } }
        ]
      });
    } catch (e) { console.warn('Mark blocked failed', e); }
  }

  showDetailsModal(id, index = null) {
    try { console.log('ShowDetails clicked', { id, index }); } catch(_) {}
    let initiative = null;
    if (id) {
      initiative = this.initiatives.find(i => String(i.id) === String(id));
    }
    if (!initiative && index !== null && index !== undefined) {
      const i = Number(index);
      if (!Number.isNaN(i) && this.filteredInitiatives[i]) {
        initiative = this.filteredInitiatives[i];
      }
    }
    if (!initiative) {
      window.showToast('Item not found', 'error');
      return;
    }
    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const title = initiative.title || initiative.objective || 'Initiative Details';

    // Preferred key order first, then remaining keys
    const preferred = ['type','title','objective','description','owner','priority','status','strategicFit','analysisSummary','analysisGaps','analysisAt','strategicAlignment','keyResults','stakeholders','progress','createdAt','updatedAt','id'];
    const keys = Array.from(new Set([...preferred, ...Object.keys(initiative)]));

    const rows = keys.map(k => {
      const v = initiative[k];
      let display;
      if (Array.isArray(v)) {
        display = esc(v.join(', '));
      } else if (v && typeof v === 'object') {
        display = `<code>{...}</code>`;
      } else {
        display = esc(v);
      }
      return `<tr><th style="text-align:left;white-space:nowrap;padding:6px 8px;border-bottom:1px solid var(--kone-border-light)">${esc(k)}</th><td style="padding:6px 8px;border-bottom:1px solid var(--kone-border-light)">${display || '<span style="color:#888">—</span>'}</td></tr>`;
    }).join('');

    const badge = (initiative.type || 'Initiative').toString().toLowerCase();
    const json = esc(JSON.stringify(initiative, null, 2));

    const content = `
      <div>
        <div style="margin-bottom:10px">
          <span class="type-badge type-${badge}">${esc(initiative.type || 'Initiative')}</span>
        </div>
        <!-- Per-field edit actions removed; single Edit Details retained -->
        <table class="database-table" style="width:100%;border-collapse:collapse;margin-bottom:12px">
          <tbody>
            ${rows}
          </tbody>
        </table>
        <details>
          <summary style="cursor:pointer">Show raw JSON</summary>
          <pre style="white-space:pre-wrap;max-height:50vh;margin-top:8px">${json}</pre>
        </details>
      </div>
    `;
    try {
      // Defer open until after current click settles to avoid any capture/bubble side-effects
      setTimeout(() => {
        try {
          const actions = [];
          actions.push({ text: 'Edit Details', class: 'btn btn-primary btn-small', action: async () => {
            try {
              const { ModalService } = await import('../../ui-kit/ModalService.jsx');
              const { h, render } = await import('preact');
              const { EditEntryPanel } = await import('../../components/edit/EditEntryPanel.jsx');
              // Render a placeholder node inside the modal; then mount the Preact component into it
              const contentVNode = h('div', { id: 'edit-entry-host' });
              const close = ModalService.open({ title: 'Edit Details', content: contentVNode, presentation: 'sheet' });
              const onSaved = async () => {
                try { await this.refresh(); } catch(_) { this.refresh(); }
                try { this._detailsClose?.(); } catch(_) {}
                // Reopen details with fresh data
                try { this.showDetailsModal(initiative.id); } catch(_) {}
              };
              const mount = () => {
                const hostEl = document.getElementById('edit-entry-host');
                if (hostEl) {
                  render(h(EditEntryPanel, { kind: 'initiative', id: String(initiative.id), onClose: close, onSaved, initial: initiative }), hostEl);
                } else {
                  // Try again on next frame if DOM not ready yet
                  requestAnimationFrame(mount);
                }
              };
              mount();
            } catch (e) { console.error('Edit details open failed', e); }
          }});
          // No explicit creation buttons in this view per requirements
          actions.push({ text: 'Close', class: 'btn btn-ghost btn-small', action: () => {} });
          const closeDetails = ModalService.open({ title: `${esc(title)}`, content, actions });
          try { this._detailsClose = closeDetails; } catch(_) {}
        } catch (err) {
          console.warn('ModalService open failed (deferred), using inline panel fallback', err);
          this.showInlineDetailsPanel(initiative);
        }
      }, 0);
    } catch (e) {
      console.warn('ModalService open failed, using inline panel fallback', e);
      this.showInlineDetailsPanel(initiative);
    }
  }

  // Minimal inline fallback if modal overlay is blocked
  showInlineDetailsPanel(initiative) {
    const existing = document.getElementById('portfolio-inline-details');
    if (existing) existing.remove();
    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const panel = document.createElement('div');
    panel.id = 'portfolio-inline-details';
    panel.style.cssText = 'position:fixed;top:0;right:0;height:100vh;width:520px;background:#fff;border-left:1px solid #ddd;box-shadow:-8px 0 24px rgba(0,0,0,.15);z-index:2147483646;display:flex;flex-direction:column';
    const header = document.createElement('div');
    header.style.cssText = 'display:flex;justify-content:space-between;align-items:center;padding:12px 16px;border-bottom:1px solid #eee';
    header.innerHTML = `<strong>${esc(initiative.title || initiative.objective || 'Details')}</strong>`;
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.style.cssText = 'font-size:20px;border:none;background:none;cursor:pointer;line-height:1;padding:4px 8px';
    closeBtn.setAttribute('data-action', 'portfolio-close-inline-details');
    header.appendChild(closeBtn);
    const body = document.createElement('div');
    body.style.cssText = 'padding:12px;overflow:auto;flex:1';
    const preferred = ['type','title','objective','description','owner','priority','status','strategicFit','analysisSummary','analysisGaps','analysisAt','strategicAlignment','keyResults','stakeholders','progress','createdAt','updatedAt','id'];
    const keys = Array.from(new Set([...preferred, ...Object.keys(initiative)]));
    const rows = keys.map(k => {
      const v = initiative[k];
      let display = '';
      if (Array.isArray(v)) display = esc(v.join(', '));
      else if (v && typeof v === 'object') display = '{...}';
      else display = esc(v);
      return `<tr><th style="text-align:left;padding:6px 8px;border-bottom:1px solid #eee;white-space:nowrap">${esc(k)}</th><td style="padding:6px 8px;border-bottom:1px solid #eee">${display || '<span style="color:#888">—</span>'}</td></tr>`;
    }).join('');
    const controls = (Config.FEATURES?.DEPENDENCIES || Config.FEATURES?.RISKS)
      ? `<div style=\"display:flex;gap:8px;justify-content:flex-end;margin:8px 0\">
           ${Config.FEATURES?.DEPENDENCIES ? `<button class=\"btn btn-ghost btn-small\" data-action=\"portfolio-edit-deps\" data-id=\"${esc(initiative.id)}\">Edit Dependencies</button>` : ''}
           ${Config.FEATURES?.RISKS ? `<button class=\"btn btn-ghost btn-small\" data-action=\"portfolio-edit-risk\" data-id=\"${esc(initiative.id)}\">Edit Risk</button>` : ''}
         </div>`
      : '';
    body.innerHTML = `<table style="width:100%;border-collapse:collapse">${rows}</table>`;
    panel.appendChild(header);
    panel.appendChild(body);
    document.body.appendChild(panel);
  }

  // New conversational analysis entrypoint
  async runNarrativeAnalysis() {
    const prompt = this.buildNarrativePrompt();
    // Use placeholder streaming to avoid overwriting the welcome message
    await this.callAI(prompt, true);
  }

  async sendUserPrompt(userPrompt) {
    const prompt = this.buildNarrativePrompt(userPrompt);
    this.appendMessage('user', userPrompt);
    await this.callAI(prompt, true);
  }

  buildNarrativePrompt(userPrompt = '') {
    const uploaded = StorageService.get('uploadedStrategy', null);
    const pillars = Array.isArray(uploaded?.structured?.pillars)
      ? uploaded.structured.pillars.map(p => p.name || '').filter(Boolean).slice(0, 20)
      : [];

    const initiatives = this.initiatives.map(i => ({
      id: i.id,
      title: i.title || '',
      type: i.type || 'Initiative',
      status: i.status || '',
      strategicFit: i.strategicFit ?? null,
      objective: i.objective || '',
      strategicAlignment: i.strategicAlignment || ''
    }));

    const portfolioBlob = JSON.stringify(initiatives).slice(0, 12000);

    const instructions = [
      'Provide a conversational, plain‑paragraph analysis of this portfolio relative to the Uploaded Strategy.',
      'Explain how initiatives support the pillars and where there are gaps or over/under‑coverage.',
      'Do not include risks or recommendations sections.',
      'Avoid headings and bullet lists. Use a natural, executive tone.'
    ].join(' ');

    const request = userPrompt && userPrompt.trim() ? `\n\nUser request: ${userPrompt.trim()}` : '\n\nUser request: Run a full portfolio analysis narrative.';

    return `Context for analysis.\n\nUploaded Strategy Pillars: ${pillars.length ? pillars.join(', ') : 'No pillars provided'}\n\nPortfolio (JSON, compact):\n${portfolioBlob}\n\nTask: ${instructions}${request}`;
  }

  async callAI(prompt, appendPlaceholder = false) {
    try {
      const cfg = PromptService.getApiConfig('portfolioAnalysis');
      window.showToast('Running portfolio analysis...', 'info');

      if (appendPlaceholder) this.appendMessage('assistant', '');

      const response = await ApiService.call(prompt, {
        maxTokens: Math.min(1600, cfg.maxTokens || 1600),
        temperature: cfg.temperature ?? 0.5,
        promptKey: 'portfolioAnalysis',
        promptVersion: cfg.promptVersion,
        systemRole: cfg.systemRole,
        stream: true,
        onToken: (_chunk, full) => {
          this.updateLastAssistantMessage(full);
        }
      });

      const text = response?.content || '';
      if (!appendPlaceholder) {
        this.appendMessage('assistant', text);
      } else if (!text) {
        this.updateLastAssistantMessage('');
      }

      this.history.push({ role: 'user', content: prompt });
      this.history.push({ role: 'assistant', content: text });
    } catch (e) {
      console.error('Portfolio analysis failed:', e);
      window.showToast('Portfolio analysis failed: ' + (e.message || 'Unknown error'), 'error');
      if (appendPlaceholder) this.updateLastAssistantMessage('Sorry, I couldn\'t complete the analysis.');
    }
  }

  async _runAnalysisLegacy() {
    try {
      window.showToast('Running portfolio analysis...', 'info');
      const initiatives = this.initiatives.map(i => ({
        id: i.id,
        title: i.title || '',
        objective: i.objective || '',
        type: i.type || 'Initiative',
        priority: i.priority || 'medium',
        strategicAlignment: i.strategicAlignment || ''
      }));
      const cfg = PromptService.getApiConfig('portfolioAnalysis');
      const uploaded = StorageService.get('uploadedStrategy', null);
      const pillars = Array.isArray(uploaded?.structured?.pillars)
        ? uploaded.structured.pillars.map(p => p.name || '').filter(Boolean).slice(0, 20)
        : [];
      if (!pillars.length) {
        window.showToast('Upload a strategy first to score strategic fit.', 'warning');
        return;
      }
      const userPrompt = `Given the following initiatives (JSON), evaluate strategic fit (0-100) for each initiative against the organization's Uploaded Strategy pillars.
Return STRICT JSON with shape: { "results": [{"id": string, "strategicFit": number}], "summary": string, "gaps": [string] }.
Prioritize alignment with these pillars (if any): ${pillars.length ? pillars.join(', ') : 'No pillars provided'}.
Initiatives:\n${JSON.stringify(initiatives).substring(0, 12000)}\n`;
      const response = await ApiService.call(userPrompt, { maxTokens: 1200, temperature: 0.2, promptKey: 'portfolioAnalysis', promptVersion: cfg.promptVersion, systemRole: cfg.systemRole, response_format: { type: 'json_object' } });
      const text = response?.content || '';
      // Try robust JSON extraction
      let results = [];
      let summaryText = '';
      let gapsList = [];
      try {
        const obj = (await import('../../utils/schema.js')).extractJSONObject(text);
        if (Array.isArray(obj)) {
          results = obj;
        } else if (obj && typeof obj === 'object') {
          const key = ['results','items','data','list'].find(k => Array.isArray(obj[k]));
          if (key) results = obj[key];
          // Capture optional summary/gaps
          if (typeof obj.summary === 'string') summaryText = obj.summary;
          if (Array.isArray(obj.gaps)) gapsList = obj.gaps.map(String);
        }
      } catch {}
      // Fallback: find first JSON array in text
      if (!Array.isArray(results) || results.length === 0) {
        const arrMatch = text.match(/\[[\s\S]*\]/);
        if (arrMatch) {
          try { results = JSON.parse(arrMatch[0]); } catch {}
        }
      }
      if (!Array.isArray(results) || results.length === 0) throw new Error('No JSON array found in AI response');
      // Baseline fallback for any missing ids to ensure manually-entered items get a score
      const pillarTokens = pillars.map(p => p.toLowerCase());
      const baselineMap = new Map();
      const contains = (txt, token) => txt.includes(token);
      initiatives.forEach(item => {
        const txt = `${item.title} ${item.objective} ${item.strategicAlignment}`.toLowerCase();
        let score = 30;
        if (pillarTokens.length) {
          const hits = pillarTokens.filter(t => contains(txt, t)).length;
          score = hits >= 2 ? 80 : (hits === 1 ? 60 : 40);
        }
        baselineMap.set(item.id, score);
      });

      let updated = 0;
      const rated = new Set();
      for (const r of results) {
        const fit = Math.max(0, Math.min(100, parseInt(r.strategicFit)));
        if (r.id && !Number.isNaN(fit)) {
          rated.add(r.id);
          try { await InitiativeService.updateInitiative(r.id, { strategicFit: fit, analysisSummary: summaryText || undefined, analysisGaps: gapsList || undefined, analysisAt: new Date().toISOString() }); updated++; } catch {}
        }
      }
      // Fill missing ids with baseline estimates
      if (pillarTokens.length) {
        for (const [id, score] of baselineMap.entries()) {
          if (!rated.has(id)) {
            try { await InitiativeService.updateInitiative(id, { strategicFit: score, analysisSummary: summaryText || undefined, analysisGaps: gapsList || undefined, analysisAt: new Date().toISOString() }); updated++; } catch {}
          }
        }
      }
      window.showToast(`Analysis complete! Updated ${updated} items.`, 'success');
      this.loadInitiatives();
      this.render();
    } catch (e) {
      console.error('Portfolio analysis failed:', e);
      window.showToast('Portfolio analysis failed: ' + e.message, 'error');
    }
  }

  exportReport() {
    window.showToast('Generating PDF report...', 'info');
    // Export functionality
  }

  exportData() {
    window.showToast('Exporting to CSV...', 'info');
    // CSV export functionality
  }

  viewDetails(id) {
    const initiative = this.initiatives.find(i => i.id === id);
    if (initiative) {
      window.showToast(`Viewing: ${initiative.title || 'Initiative'}`, 'info');
    }
  }

  editInitiative(id) {
    const initiative = this.initiatives.find(i => i.id === id);
    if (initiative) {
      window.showToast(`Editing: ${initiative.title || 'Initiative'}`, 'info');
    }
  }

  openBulkUpload() {
    // Open the bulk upload modal
    bulkUpload.open();
  }

  activate() {
    console.log('Portfolio module activated');
    this.loadInitiatives();
    // If already mounted, re-render; mounting is handled by TabLayout
    if (this.container) {
      this.render();
    }
  }
}
